// ---------------------------------------------------------------------------
// Insights content  - Flora Dine
// ---------------------------------------------------------------------------
// Single source of truth for the Insights feature. Used by the listing page
// and the /insights/[slug] detail pages. Content is brand-owned and themed
// around two pillars: an honest look at the biodegradable industry (pros &
// cons) and our identity as a women-led, fair-employment organisation.
//
// Images are local /assets so they always render and stay on-brand. Author
// avatars use initials (no stock portraits).
// ---------------------------------------------------------------------------

export type InsightCategory = 'Industry' | 'Women & Impact' | 'Sustainability' | 'Compliance';

export interface InsightAuthor {
  name: string;
  role: string;
  initials: string;
}

export interface Insight {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: InsightCategory;
  readTime: number;
  publishDate: string;
  author: InsightAuthor;
  image: string;
  alt: string;
  isFeatured?: boolean;
  /** Markdown body rendered on the detail page. */
  content: string;
}

// ── Author personas (women-led leadership) ─────────────────────────────────
const AANYA: InsightAuthor = { name: 'Aanya Verma', role: 'Founder & CEO', initials: 'AV' };
const NISHA: InsightAuthor = {
  name: 'Dr. Nisha Rao',
  role: 'Head of Sustainability',
  initials: 'NR',
};
const MEERA: InsightAuthor = {
  name: 'Meera Iyer',
  role: 'Export & Compliance Lead',
  initials: 'MI',
};
const PRIYA: InsightAuthor = {
  name: 'Priya Nair',
  role: 'Community & Impact Lead',
  initials: 'PN',
};

export const insights: Insight[] = [
  {
    id: 1,
    slug: 'pros-cons-biodegradable-industry',
    title: 'The Honest Truth: Pros & Cons of the Biodegradable Industry',
    excerpt:
      'Biodegradable foodware is not magic  - it is a trade-off. Here is a candid look at where compostable tableware genuinely wins, and where buyers should stay sharp.',
    category: 'Industry',
    readTime: 8,
    publishDate: 'Feb 18, 2026',
    author: NISHA,
    image: '/assets/images/process.jpg',
    alt: 'Bagasse foodware moving through the Flora Dine production line',
    isFeatured: true,
    content: `The biodegradable foodware industry is growing fast  - and with growth comes hype. As a manufacturer and exporter, we think buyers deserve a straight answer rather than a sales pitch. Compostable tableware is a genuine improvement over single-use plastic, but it is a trade-off, not a miracle. Here is the honest version.

## The case for biodegradable foodware

- **It diverts agricultural waste.** Our plates start as sugarcane bagasse and areca leaf  - by-products that are otherwise burned or dumped. Turning them into foodware lowers open-field emissions and uses no trees and no petroleum.
- **It composts in about 90 days.** In industrial composting, certified bagasse breaks down in roughly 60–90 days and returns to soil, versus centuries for plastic that fragments into microplastics.
- **It meets the regulations buyers actually face.** EN 13432 / OK Compost certification and PFAS-free formulations line up with the EU Single-Use Plastics Directive, the 2026 PPWR, and UK rules.
- **It is food-safe and practical.** Modern bagasse handles hot, wet and oily foods, is microwave- and freezer-friendly, and carries no plastic taste.
- **It supports rural livelihoods.** Sourcing crop residue puts income back into farming communities  - a social return plastic cannot offer.

## The honest drawbacks

- **It costs more per unit.** Compostable items are typically pricier than conventional plastic. The gap narrows at volume, but it is real and worth budgeting for.
- **It usually needs industrial composting.** "Compostable" rarely means "throw it in your garden." Without access to commercial composting, end-of-life benefits shrink.
- **Performance has limits.** Areca suits dry and event use; bagasse handles heat and moisture better. Choosing the wrong material for the application disappoints.
- **Quality varies wildly between suppliers.** The market is crowded with uncertified product. Vetting  - certificates, lab reports, factory audits  - is essential.
- **Greenwashing is everywhere.** "Eco", "natural" and "green" are unregulated words. Only certified claims (EN 13432, BPI, OK Compost, PFAS-free testing) mean anything.
- **It is moisture-sensitive in storage.** Shelf life and packaging need care, especially for long sea freight.

## How Flora Dine answers the cons

We do not pretend the drawbacks away  - we engineer around them. Every batch ships with compostability and PFAS-free documentation, our QC rejects sub-spec product before it leaves the floor, and we help buyers match material to use-case so performance never surprises them. Honest sourcing is the whole point.

The takeaway: biodegradable foodware is the right direction for most food-service buyers  - provided you buy certified product, plan for composting, and pick the right material for the job.`,
  },
  {
    id: 2,
    slug: 'women-led-empowered-organisation',
    title: 'Women at the Heart of Flora Dine',
    excerpt:
      'Flora Dine is a women-led organisation built on fair wages, skills, and dignity. Meet the people who turn agricultural waste into premium, planet-friendly foodware.',
    category: 'Women & Impact',
    readTime: 6,
    publishDate: 'Feb 10, 2026',
    author: AANYA,
    image: '/assets/images/about.png',
    alt: 'Members of the women-led Flora Dine team at work',
    content: `Flora Dine was founded on a simple conviction: a sustainable product should also build a sustainable community. We are a **women-led organisation**, and that is not a marketing line  - it shapes who makes our products, how they are paid, and how decisions are made.

## Led by women, run with women

From founder leadership to the production floor, women hold the roles that matter at Flora Dine  - in operations, quality, sustainability and export. Leadership that reflects the workforce makes better decisions for it.

## Our commitments

- **Fair, equal wages.** Equal pay for equal work, paid on time, every time.
- **Skills and training.** On-the-job training in manufacturing, quality and export readiness, so careers grow with the company.
- **Dignity and safety.** Safe facilities, reasonable hours, and a workplace built on respect.
- **Rural livelihoods.** By sourcing crop residue and hiring locally, we keep income inside the communities that grow our raw material.

## Why it matters for buyers

When you place an order with Flora Dine, you are not only choosing certified compostable foodware  - you are backing a supply chain where the people behind the product are treated well. For importers and brands that care about both environmental and social impact, that story is part of the value.

Empowerment is not a side project here. It is how the company is built  - and it is why we are proud of every plate that leaves our floor.`,
  },
  {
    id: 3,
    slug: 'bagasse-vs-areca-materials',
    title: 'Bagasse vs Areca: Choosing the Right Compostable Material',
    excerpt:
      'Two natural materials, two strengths. A practical guide to when sugarcane bagasse wins and when areca leaf is the smarter choice for your menu.',
    category: 'Sustainability',
    readTime: 5,
    publishDate: 'Jan 28, 2026',
    author: NISHA,
    image: '/assets/images/products.jpeg',
    alt: 'Bagasse and areca tableware side by side',
    content: `Buyers often ask which material is "better"  - bagasse or areca. The honest answer is that they are different tools for different jobs.

## Sugarcane bagasse

Made from the fibrous pulp left after juice extraction, bagasse is moulded under heat and pressure into smooth, sturdy tableware.

- **Best for:** hot, wet and oily foods; microwave and freezer use; high-volume food service.
- **Strengths:** durable, leak-resistant, clean modern finish, consistent sizing.

## Areca leaf

Pressed from naturally fallen areca palm sheaths, areca foodware has a distinctive grain and a premium, rustic look.

- **Best for:** events, catering, dry and ambient-temperature foods, premium presentation.
- **Strengths:** striking natural appearance, chemical-free, every piece unique.

## Choosing well

If your menu is hot and saucy or needs reheating, lead with bagasse. If you are styling an event or want a natural showpiece, areca shines. Many of our clients stock both. Either way, both are renewable, tree-free and certified compostable  - the choice is about fit, not sustainability.`,
  },
  {
    id: 4,
    slug: 'inside-90-day-compost-cycle',
    title: 'Inside the 90-Day Compost Cycle',
    excerpt:
      'What actually happens after a compostable plate is thrown away? A clear, no-jargon walk through how certified bagasse returns to soil.',
    category: 'Sustainability',
    readTime: 5,
    publishDate: 'Jan 15, 2026',
    author: PRIYA,
    image: '/assets/images/homeNew.jpg',
    alt: 'Compostable foodware breaking down into soil',
    content: `"Compostable" is a precise, testable claim  - not a vibe. Here is what the 90-day cycle really involves.

## What the standard requires

EN 13432, the European benchmark, requires a product to biodegrade at least 90% within six months and to physically disintegrate within three months under industrial composting, leaving no toxic residue.

## The cycle, step by step

1. **Collection.** Used foodware goes into a commercial composting stream  - not landfill, where conditions stall breakdown.
2. **Disintegration.** Heat, moisture and microbes break the material into small fragments within weeks.
3. **Biodegradation.** Microorganisms convert those fragments into CO2, water and biomass.
4. **Maturation.** What remains becomes nutrient-rich compost that enriches soil.

## The honest caveat

This depends on **industrial** composting. Home compost heaps are cooler and slower, and landfill is anaerobic  - so access to the right facility is what unlocks the benefit. It is the single most important thing buyers can plan for.`,
  },
  {
    id: 5,
    slug: 'exporting-to-eu-2026',
    title: 'Exporting to the EU in 2026: PPWR, PFAS & EN 13432',
    excerpt:
      'The compliance landscape for compostable foodware is tightening. A buyer-friendly summary of the rules that matter for EU and UK imports this year.',
    category: 'Compliance',
    readTime: 7,
    publishDate: 'Jan 6, 2026',
    author: MEERA,
    image: '/assets/images/productsOld.jpg',
    alt: 'Export documentation and certified compostable foodware',
    content: `Regulation is now a competitive advantage, not just a hurdle. Here is what importers buying compostable foodware for the EU and UK should have on their radar in 2026.

## The rules that matter

- **Single-Use Plastics Directive.** Continues to push food service away from conventional plastic and toward certified alternatives.
- **PPWR (Packaging & Packaging Waste Regulation).** Tightens recyclability and compostability expectations and reinforces EPR obligations.
- **PFAS restrictions.** "Forever chemicals" are being phased out  - PFAS-free testing is increasingly non-negotiable.
- **EN 13432 / OK Compost.** The proof that a compostability claim is real.

## What we provide

For every shipment we supply the document set customs and brokers expect: commercial invoice, packing list, certificate of origin, EN 13432 / compostability certificates, and PFAS-free lab reports  - with phytosanitary certification for areca where required.

## The buyer's checklist

Ask every supplier for current certificates, verify them with the issuing body, and confirm PFAS-free testing. If a supplier cannot produce paperwork, treat the "eco" claim as unproven. Compliance is exactly where the cheap, uncertified end of the market falls down.`,
  },
  {
    id: 6,
    slug: 'zero-waste-events-case-study',
    title: 'Zero-Waste Events: A Switch That Paid Off',
    excerpt:
      'How a large event caterer moved from plastic to certified bagasse and areca  - what improved, what was tricky, and what they would tell other buyers.',
    category: 'Industry',
    readTime: 6,
    publishDate: 'Dec 18, 2025',
    author: PRIYA,
    image: '/assets/images/products.jpeg',
    alt: 'Event catering served on compostable bagasse and areca tableware',
    content: `When a regional event caterer set a zero-waste goal, they swapped their plastic and foam service for certified Flora Dine foodware. The move was a success  - and instructive about the real-world trade-offs.

## What improved

- **Waste diverted from landfill** through an industrial composting partner.
- **Guest perception lifted**  - the natural areca presentation read as premium, not "disposable".
- **A cleaner sustainability story** they could put in front of their own clients, backed by certificates.

## What took planning

- **Unit cost was higher**, offset partly by simplified waste handling and a marketable green credential.
- **Composting logistics had to be arranged up front**  - the benefit only lands with the right end-of-life stream.
- **Material was matched to service:** bagasse for hot mains, areca platters for display.

## Their advice to other buyers

Start with one event, line up composting before you switch, insist on certified product, and pick materials by use-case. Done that way, the switch holds up  - environmentally and commercially.`,
  },
];

// ── On-page Pros & Cons section data ───────────────────────────────────────
export const industryPros: { title: string; detail: string }[] = [
  {
    title: 'Diverts agricultural waste',
    detail:
      'Bagasse and areca are crop by-products  - no trees, no petroleum, lower open-field emissions.',
  },
  {
    title: 'Composts in ~90 days',
    detail:
      'Certified material returns to soil in industrial composting, versus centuries for plastic.',
  },
  {
    title: 'Regulation-ready',
    detail: 'EN 13432 / PFAS-free formulations align with EU SUPD, PPWR and UK rules.',
  },
  {
    title: 'Food-safe & practical',
    detail: 'Handles hot, wet and oily foods; microwave- and freezer-friendly; no plastic taste.',
  },
  {
    title: 'Supports livelihoods',
    detail: 'Sourcing crop residue puts income back into farming communities.',
  },
];

export const industryCons: { title: string; detail: string }[] = [
  {
    title: 'Higher unit cost',
    detail: 'Pricier than conventional plastic, though the gap narrows at volume.',
  },
  {
    title: 'Needs industrial composting',
    detail: 'Home compost is slower; the end-of-life benefit depends on the right facility.',
  },
  {
    title: 'Performance has limits',
    detail:
      'Areca suits dry/event use; bagasse handles heat and moisture  - match material to job.',
  },
  {
    title: 'Quality varies by supplier',
    detail: 'A crowded market means vetting  - certificates, lab reports, audits  - is essential.',
  },
  {
    title: 'Greenwashing risk',
    detail: 'Only certified claims (EN 13432, BPI, OK Compost, PFAS-free) actually mean anything.',
  },
];

// ── Women-Led Impact section data ──────────────────────────────────────────
// Framed for the *buyer*  - the commercial value of our social story. The
// internal team & culture view lives on the About page (CultureSection); the
// deep narrative is the `women-led-empowered-organisation` article.
export const womenLedValues: { icon: string; title: string; detail: string }[] = [
  {
    icon: 'HeartIcon',
    title: 'Impact in every order',
    detail: 'Each purchase funds fair wages and training for a women-led workforce.',
  },
  {
    icon: 'DocumentCheckIcon',
    title: 'An ESG story you can share',
    detail: 'Environmental and social impact from one supplier  - ready for your own reporting.',
  },
  {
    icon: 'GlobeEuropeAfricaIcon',
    title: 'Ethical, traceable sourcing',
    detail: 'Crop-residue sourcing and local hiring keep income inside farming communities.',
  },
  {
    icon: 'UserGroupIcon',
    title: 'Led by women, end to end',
    detail: 'Women run operations, quality, sustainability and export  - not just the storefront.',
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────
export const insightCategories: ('All' | InsightCategory)[] = [
  'All',
  'Industry',
  'Women & Impact',
  'Sustainability',
  'Compliance',
];

export const getInsightBySlug = (slug: string): Insight | undefined =>
  insights.find((i) => i.slug === slug);

export const getAllInsightSlugs = (): string[] => insights.map((i) => i.slug);
